import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { trackEvent, getAttribution, getSessionId } from "@/lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#4E5A3B]/60 border border-white/15 hover:border-white/25 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3.5 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F] transition-colors";
const labelCls =
  "text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9C6B4] mb-1.5 block";

const LeadForm = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const formViewFired = useRef(false);
  const firstInteractionFired = useRef(false);

  useEffect(() => {
    if (formViewFired.current) return;
    formViewFired.current = true;
    trackEvent("form_view", { form_id: "lead-form" });
  }, []);

  const onFirstInteraction = () => {
    if (firstInteractionFired.current) return;
    firstInteractionFired.current = true;
    trackEvent("form_start", { form_id: "lead-form" });
    trackEvent("form_first_interaction", { form_id: "lead-form" });
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      mobile: "",
      email: "",
      city: "",
      landline: "",
      message: "",
      dsgvo: false,
      website: "", // honeypot
    },
  });

  const dsgvo = watch("dsgvo");

  const onSubmit = async (data) => {
    if (!data.dsgvo) {
      toast.error("Bitte stimme der Datenschutzerklärung zu.");
      return;
    }
    setSubmitting(true);
    try {
      const attribution = { ...getAttribution(), session_id: getSessionId(), user_agent: navigator.userAgent, language: navigator.language };
      const res = await axios.post(`${API}/leads`, { ...data, attribution }, { timeout: 15000 });
      const leadId = res.data?.id;
      const convToken = res.data?.conversion_token;
      trackEvent("generate_lead", { form_id: "lead-form", lead_id: leadId });
      trackEvent("form_submit", { form_id: "lead-form", lead_id: leadId });
      setSuccess(true);
      toast.success("Deine Anfrage wurde gesendet!");
      reset();
      setTimeout(() => navigate(convToken ? `/danke?t=${encodeURIComponent(convToken)}` : "/danke"), 700);
    } catch (e) {
      trackEvent("form_submit_error", { form_id: "lead-form", error: e?.response?.status || "network" });
      console.error(e);
      toast.error(
        e?.response?.data?.detail ||
          "Etwas ist schiefgelaufen. Bitte versuche es erneut oder schreibe uns auf WhatsApp."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      id="lead-form"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative"
    >
      <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[#E8E5D0]/40 via-transparent to-[#25D366]/30 blur-[2px] opacity-60" />

      <div className="relative glass-strong rounded-2xl p-6 sm:p-8 lg:p-9">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full bg-[#E8E5D0] animate-pulse" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E8E5D0]">
            Kostenlose Analyse
          </span>
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-black text-[#F3F1E8] tracking-tight leading-tight mb-2">
          Jetzt kostenlos beraten lassen.
        </h3>
        <p className="text-sm text-[#C9C6B4] mb-6">
          Antwort innerhalb von 24 Stunden – meist sofort per WhatsApp.
        </p>

        {success ? (
          <div className="flex flex-col items-center text-center py-8" data-testid="form-success">
            <CheckCircle2 size={56} className="text-[#E8E5D0] mb-4" />
            <h4 className="font-display text-2xl font-black mb-2">Geschafft!</h4>
            <p className="text-[#C9C6B4] text-sm">
              Wir leiten dich gleich auf die Bestätigungsseite weiter…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} onFocus={onFirstInteraction} className="space-y-4" data-testid="lead-form">
            {/* Honeypot */}
            <input
              type="text"
              autoComplete="off"
              tabIndex={-1}
              {...register("website")}
              className="hidden"
              aria-hidden="true"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Vorname *</label>
                <input
                  {...register("first_name", { required: true, minLength: 2 })}
                  className={inputCls}
                  placeholder="Max"
                  data-testid="input-first-name"
                />
                {errors.first_name && (
                  <p className="text-xs text-red-400 mt-1">Bitte Vornamen eingeben.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Nachname *</label>
                <input
                  {...register("last_name", { required: true, minLength: 2 })}
                  className={inputCls}
                  placeholder="Mustermann"
                  data-testid="input-last-name"
                />
                {errors.last_name && (
                  <p className="text-xs text-red-400 mt-1">Bitte Nachnamen eingeben.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Mobilnummer *</label>
                <input
                  type="tel"
                  {...register("mobile", { required: true, minLength: 5 })}
                  className={inputCls}
                  placeholder="+49 176 0000000"
                  data-testid="input-mobile"
                />
                {errors.mobile && (
                  <p className="text-xs text-red-400 mt-1">Bitte Mobilnummer eingeben.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Festnetz (optional)</label>
                <input
                  type="tel"
                  {...register("landline")}
                  className={inputCls}
                  placeholder="06106 0000000"
                  data-testid="input-landline"
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>E-Mail *</label>
              <input
                type="email"
                {...register("email", {
                  required: true,
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                })}
                className={inputCls}
                placeholder="max@email.de"
                data-testid="input-email"
              />
              {errors.email && (
                <p className="text-xs text-red-400 mt-1">Bitte gültige E-Mail eingeben.</p>
              )}
            </div>

            <div>
              <label className={labelCls}>Wohnort *</label>
              <input
                {...register("city", { required: true, minLength: 2 })}
                className={inputCls}
                placeholder="Rodgau"
                data-testid="input-city"
              />
              {errors.city && (
                <p className="text-xs text-red-400 mt-1">Bitte Wohnort eingeben.</p>
              )}
            </div>

            <div>
              <label className={labelCls}>Nachricht (optional)</label>
              <textarea
                {...register("message")}
                rows={3}
                className={inputCls + " resize-none"}
                placeholder="Erzähl uns kurz von deinem Ziel…"
                data-testid="input-message"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer select-none pt-1">
              <Checkbox
                checked={dsgvo}
                onCheckedChange={(v) => setValue("dsgvo", !!v, { shouldValidate: true })}
                className="mt-1 border-[rgba(255,255,255,0.28)] data-[state=checked]:bg-[#E8E5D0] data-[state=checked]:text-[#3A4230] data-[state=checked]:border-[#E8E5D0]"
                data-testid="checkbox-dsgvo"
              />
              <span className="text-xs text-[#C9C6B4] leading-relaxed">
                Ich stimme der{" "}
                <a
                  href="/datenschutz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E8E5D0] hover:underline"
                >
                  Datenschutzerklärung
                </a>{" "}
                zu und willige in die Kontaktaufnahme zur Bearbeitung meiner Anfrage ein. *
              </span>
            </label>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={submitting}
              className="group w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-4 flex items-center justify-center gap-2 transition-all"
              data-testid="submit-lead-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Sende…
                </>
              ) : (
                <>
                  Jetzt kostenlos beraten lassen
                  <ArrowRight
                    size={18}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </>
              )}
            </motion.button>

            <p className="text-[11px] text-[#A8A58F] text-center">
              100% unverbindlich · DSGVO-konform · Keine Spam-Mails
            </p>
          </form>
        )}
      </div>
    </motion.div>
  );
};

export default LeadForm;
